import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useValidationMessage } from "@/shared/lib/i18n/validation-message.ts";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";
import { useUpdateMachineSettings } from "@/machines/api/queries/machines.queries";
import toast from "react-hot-toast";

export interface MachineTargetsModalPayload {
  machineId: number;
  machineName: string;
  loading_time_target_s?: number;
  waiting_time_target_s?: number;
  buckets_per_truck_target?: number;
}

interface MachineTargetsModalProps {
  payload: MachineTargetsModalPayload;
  onClose: () => void;
}

interface TargetFormValues {
  loading_time_target_s: number | undefined;
  waiting_time_target_s: number | undefined;
  buckets_per_truck_target: number | undefined;
}

const MachineTargetsModal = ({ payload, onClose }: MachineTargetsModalProps) => {
  const { t } = useTranslation("machines");
  const updateSettings = useUpdateMachineSettings();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<TargetFormValues>({
    defaultValues: {
      loading_time_target_s: payload.loading_time_target_s,
      waiting_time_target_s: payload.waiting_time_target_s,
      buckets_per_truck_target: payload.buckets_per_truck_target,
    },
  });
  const validationMessage = useValidationMessage();

  const onSubmit = async (data: TargetFormValues) => {
    try {
      await updateSettings.mutateAsync({
        id: payload.machineId,
        settings: {
          loading_time_target_s: data.loading_time_target_s,
          waiting_time_target_s: data.waiting_time_target_s,
          buckets_per_truck_target: data.buckets_per_truck_target,
        },
      });
      toast.success(t("targetsModal.toast.success"));
      onClose();
    } catch {
      toast.error(t("targetsModal.toast.error"));
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-4">
      <div className="space-y-2">
        <Label htmlFor="loading_time_target_s">{t("settingsForm.fields.loadingTargetLabel")}</Label>
        <Input
          id="loading_time_target_s"
          type="number"
          {...register("loading_time_target_s", {
            valueAsNumber: true,
            min: { value: 0, message: "validation.mustBePositive" },
          })}
          placeholder={t("settingsForm.fields.loadingTargetPlaceholder")}
        />
        {errors.loading_time_target_s && (
          <p className="text-sm text-destructive">{validationMessage(errors.loading_time_target_s.message)}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="waiting_time_target_s">{t("settingsForm.fields.waitingTargetLabel")}</Label>
        <Input
          id="waiting_time_target_s"
          type="number"
          {...register("waiting_time_target_s", {
            valueAsNumber: true,
            min: { value: 0, message: "validation.mustBePositive" },
          })}
          placeholder={t("settingsForm.fields.waitingTargetPlaceholder")}
        />
        {errors.waiting_time_target_s && (
          <p className="text-sm text-destructive">{validationMessage(errors.waiting_time_target_s.message)}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="buckets_per_truck_target">{t("settingsForm.fields.bucketsPerTruckLabel")}</Label>
        <Input
          id="buckets_per_truck_target"
          type="number"
          {...register("buckets_per_truck_target", {
            valueAsNumber: true,
            min: { value: 1, message: "validation.atLeastOne" },
          })}
          placeholder={t("settingsForm.fields.bucketsPerTruckPlaceholder")}
        />
        {errors.buckets_per_truck_target && (
          <p className="text-sm text-destructive">{validationMessage(errors.buckets_per_truck_target.message)}</p>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>
          {t("actions.cancel")}
        </Button>
        <Button type="submit" disabled={!isDirty || updateSettings.isPending}>
          {updateSettings.isPending ? t("settingsForm.actions.saving") : t("targetsModal.submit")}
        </Button>
      </div>
    </form>
  );
};

export default MachineTargetsModal;
